import React, { useState } from 'react';
import { 
  Search, Filter, Download, ArrowUpRight, ArrowDownRight, Wallet,
  ChevronLeft, ChevronRight, Calendar, User, TrendingUp,
  Clock, CheckCircle, XCircle, AlertCircle, Eye,
  DollarSign, Banknote, Smartphone, Landmark, Users,
  FileText
} from 'lucide-react';

import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminPayouts, updatePayoutStatus } from '../../redux/adminPayoutsSlice';

const Payouts = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const dispatch = useDispatch();
  const { payouts, loading, error } = useSelector((state) => state.adminPayouts);

  React.useEffect(() => {
    dispatch(fetchAdminPayouts());
  }, [dispatch]);

  const stats = {
    totalPaid: payouts.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0),
    pendingAmount: payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0),
    refundAmount: 0,
    totalPayouts: payouts.length,
    completedCount: payouts.filter(p => p.status === 'completed').length,
    pendingCount: payouts.filter(p => p.status === 'pending').length,
    rejectedCount: payouts.filter(p => p.status === 'rejected').length
  };

  const getStatusConfig = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return { label: 'Completed', icon: CheckCircle, color: '#10b981', bg: '#d1fae5' };
      case 'pending': return { label: 'Pending', icon: Clock, color: '#f59e0b', bg: '#fed7aa' };
      case 'rejected': return { label: 'Rejected', icon: XCircle, color: '#ef4444', bg: '#fee2e2' };
      default: return { label: status, icon: AlertCircle, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'Bank Transfer': return Landmark;
      case 'UPI': return Smartphone;
      case 'Net Banking': return Landmark;
      default: return Wallet;
    }
  };

  const filteredPayouts = (payouts || []).filter(payout => {
    const sellerName = payout.seller?.storeName || payout.seller?.username || 'Unknown';
    const matchesSearch = payout._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          sellerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || payout.status === filterStatus.toLowerCase();
    const matchesMethod = filterMethod === 'all' || payout.payoutMethod === filterMethod;
    return matchesSearch && matchesStatus && matchesMethod;
  });

  const totalPages = Math.ceil(filteredPayouts.length / itemsPerPage);
  const paginatedPayouts = filteredPayouts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor, suffix }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{suffix === '₹' ? `₹${value.toLocaleString()}` : value.toLocaleString()}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const handleApprove = (payoutId) => {
    if(window.confirm('Approve this payout?')) {
      dispatch(updatePayoutStatus({ id: payoutId, status: 'completed' }));
    }
  };

  const handleReject = (payoutId) => {
    if(window.confirm('Reject this payout?')) {
      dispatch(updatePayoutStatus({ id: payoutId, status: 'rejected' }));
    }
  };

  if (loading) return <div className="p-4">Loading payouts...</div>;
  if (error) return <div className="p-4 text-danger">{error}</div>;

  return (
    <div className="admin-payouts-modern">
      {/* Header */}
      <div className="payouts-header">
        <div>
          <h1 className="page-title">Seller Payouts</h1>
          <p className="page-subtitle">Manage and approve withdrawal requests from sellers</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export Report
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Paid (All Time)" value={stats.totalPaid} icon={Wallet} color="#10b981" bgColor="#d1fae5" suffix="₹" />
        <StatCard title="Pending Requests" value={stats.pendingAmount} icon={Clock} color="#f59e0b" bgColor="#fed7aa" suffix="₹" />
        <StatCard title="This Month Paid" value={112400} icon={TrendingUp} color="#3b82f6" bgColor="#dbeafe" suffix="₹" />
        <StatCard title="Total Payouts" value={stats.totalPayouts} icon={Users} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by Payout ID or Seller Name..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Completed">Completed</option>
            <option value="Rejected">Rejected</option>
          </select>
          <select className="filter-select" value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)}>
            <option value="all">All Methods</option>
            <option value="Bank Transfer">Bank Transfer</option>
            <option value="UPI">UPI</option>
            <option value="Net Banking">Net Banking</option>
          </select>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>{filteredPayouts.length} Payout Requests Found</span>
      </div>

      {/* Payouts Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="payouts-table">
            <thead>
              <tr>
                <th>Payout ID</th>
                <th>Date & Time</th>
                <th>Seller</th>
                <th>Method</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPayouts.map((payout) => {
                const statusConfig = getStatusConfig(payout.status);
                const StatusIcon = statusConfig.icon;
                const MethodIcon = getMethodIcon(payout.payoutMethod);
                
                return (
                  <tr key={payout._id}>
                    <td className="payout-id">#{payout._id.substring(payout._id.length - 6).toUpperCase()}</td>
                    <td className="date-cell">
                      <div className="date-info">
                        <Calendar size={12} />
                        <span>{new Date(payout.createdAt).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td className="seller-cell">
                      <div className="seller-avatar">
                        <span>{payout.seller?.username ? payout.seller.username.charAt(0).toUpperCase() : 'U'}</span>
                      </div>
                      <div className="seller-info">
                        <div className="seller-name">{payout.seller?.storeName || payout.seller?.username || 'Unknown'}</div>
                      </div>
                    </td>
                    <td className="method-cell">
                      <span className="method-badge">
                        <MethodIcon size={12} />
                        {payout.payoutMethod || 'Unknown'}
                      </span>
                    </td>
                    <td className="amount-cell">₹{payout.amount.toLocaleString()}</td>
                    <td>
                      <span className="status-badge" style={{ backgroundColor: statusConfig.bg, color: statusConfig.color }}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="action-buttons">
                        {payout.status === 'pending' ? (
                          <>
                            <button className="action-btn approve" onClick={() => handleApprove(payout._id)} title="Approve">
                              <CheckCircle size={16} />
                            </button>
                            <button className="action-btn reject" onClick={() => handleReject(payout._id)} title="Reject">
                              <XCircle size={16} />
                            </button>
                          </>
                        ) : (
                          <button className="action-btn view" title="View Details">
                            <FileText size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {paginatedPayouts.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">No payouts found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredPayouts.length)} of {filteredPayouts.length} payouts
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default Payouts;